import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, Marker, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import { X, Navigation, Clock, ShoppingCart, Loader2, Footprints, Car } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Coordinates } from '../../types';
import { NearbySpotAmenity } from '../../services/overpassService';
import { fetchDrivingRoute, fetchWalkingRoute } from '../../services/routingService';
import { openExternalNavigation } from '../../services/navigationService';
import { BRAND } from '../../constants/mapColors';

// Rutsjebane-ikon for lekeplass (lucide har ingen passende, så vi tegner en enkel sklie).
const SlideIcon: React.FC<{ size?: number; className?: string }> = ({ size = 16, className }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M3 21h18" />
    <path d="M7 21V6h3" />
    <path d="M10 6c0 8-1 15 8 15" />
  </svg>
);

const SATELLITE_TILE = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';

const spotMarkerIcon = L.divIcon({
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#ef4444;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center">
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
  </div>`,
});

const shopMarkerIcon = (isPlayground: boolean) => L.divIcon({
  className: '',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
  html: `<div style="width:28px;height:28px;border-radius:50%;background:#0ea5e9;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center">
    ${isPlayground
      ? '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18"/><path d="M7 21V6h3"/><path d="M10 6c0 8-1 15 8 15"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>'}
  </div>`,
});

// Fit the map to show the whole route (or both endpoints).
const FitToRoute: React.FC<{ points: [number, number][] }> = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => {
      map.invalidateSize();
      if (points.length >= 2) {
        map.fitBounds(L.latLngBounds(points), { padding: [32, 32], maxZoom: 16 });
      }
    }, 120);
    return () => clearTimeout(t);
  }, [map, points]);
  return null;
};

const formatDist = (m: number) => (m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`);
const formatDuration = (s: number) => {
  const min = Math.round(s / 60);
  if (min < 60) return `~${min} min`;
  return `~${Math.floor(min / 60)} t ${String(min % 60).padStart(2, '0')} min`;
};

interface NearbyAmenityRouteOverlayProps {
  spotLocation: Coordinates;
  amenity: NearbySpotAmenity;
  kind: 'grocery' | 'playground';
  onClose: () => void;
}

const NearbyAmenityRouteOverlay: React.FC<NearbyAmenityRouteOverlayProps> = ({ spotLocation, amenity, kind, onClose }) => {
  const { t } = useTranslation();
  const isPlayground = kind === 'playground';
  const [mode, setMode] = useState<'foot' | 'driving'>('foot');
  const [routeLatLng, setRouteLatLng] = useState<[number, number][] | null>(null);
  const [routeMeters, setRouteMeters] = useState<number | null>(null);
  const [routeSeconds, setRouteSeconds] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const straightLine = useMemo<[number, number][]>(() => [
    [spotLocation.latitude, spotLocation.longitude],
    [amenity.location.latitude, amenity.location.longitude],
  ], [spotLocation, amenity.location]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setRouteLatLng(null);
    setRouteMeters(null);
    setRouteSeconds(null);
    const fetcher = mode === 'foot' ? fetchWalkingRoute : fetchDrivingRoute;
    fetcher(spotLocation, amenity.location)
      .then((res) => {
        if (cancelled) return;
        setRouteLatLng(res.coordinates.map(([lon, lat]) => [lat, lon] as [number, number]));
        setRouteMeters(res.distanceMeters);
        setRouteSeconds(res.durationSeconds);
      })
      .catch(() => { /* fall back to straight line */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [spotLocation, amenity.location, mode]);

  const line = routeLatLng && routeLatLng.length >= 2 ? routeLatLng : straightLine;
  const title = amenity.name || (isPlayground ? t('spot.proximity.playground', 'Lekeplass') : t('spot.proximity.grocery', 'Dagligvare'));

  return (
    <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative rounded-3xl overflow-hidden shadow-2xl border border-white/20 bg-[var(--bg-surface)]"
        style={{ width: 'min(92vw, 520px)', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Map tile with route overlay */}
        <div className="relative w-full" style={{ height: 'min(46vh, 320px)' }}>
          <MapContainer
            center={[spotLocation.latitude, spotLocation.longitude]}
            zoom={14}
            zoomControl={false}
            attributionControl={false}
            style={{ width: '100%', height: '100%', position: 'absolute', inset: 0 }}
          >
            <FitToRoute points={line} />
            <TileLayer url={SATELLITE_TILE} maxZoom={18} />
            <Polyline positions={line} pathOptions={{ color: BRAND, weight: 5, opacity: 0.9 }} />
            <Marker position={[spotLocation.latitude, spotLocation.longitude]} icon={spotMarkerIcon} zIndexOffset={1000} />
            <Marker position={[amenity.location.latitude, amenity.location.longitude]} icon={shopMarkerIcon(isPlayground)} />
          </MapContainer>

          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-[1000] w-9 h-9 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-all active:scale-90"
            aria-label={t('common.close', 'Lukk')}
          >
            <X size={18} />
          </button>

          {loading && (
            <div className="absolute bottom-3 left-3 z-[1000] flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-black/60 text-white text-2xs font-bold">
              <Loader2 size={12} className="animate-spin" /> {t('spot.proximity.calculating_route', 'Beregner rute…')}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            {isPlayground ? <SlideIcon size={18} className="text-sky-400 shrink-0" /> : <ShoppingCart size={18} className="text-sky-400 shrink-0" />}
            <h3 className="text-[var(--text-primary)] font-black text-sm truncate flex-1">{title}</h3>
          </div>

          {/* Gå / Kjør-toggle */}
          <div className="flex w-full bg-[var(--bg-surface-alt)] p-0.5 rounded-xl border border-[var(--border-primary)]">
            <button
              onClick={() => setMode('foot')}
              className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${mode === 'foot' ? 'bg-emerald-600 text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              <Footprints size={12} /> {t('spot.proximity.on_foot', 'Til fots')}
            </button>
            <button
              onClick={() => setMode('driving')}
              className={`flex-1 py-1.5 rounded-lg text-3xs font-black uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 ${mode === 'driving' ? 'bg-emerald-600 text-white shadow-md' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'}`}
            >
              <Car size={12} /> {t('spot.proximity.by_car', 'Kjøre')}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2 text-2xs font-bold">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-[var(--bg-surface-alt)] border border-[var(--border-primary)] text-[var(--text-secondary)]">
              {mode === 'foot' ? <Footprints size={11} className="text-emerald-400" /> : <Car size={11} className="text-emerald-400" />}
              {routeMeters != null ? formatDist(routeMeters) : formatDist(amenity.distanceM)}
              {routeSeconds != null && <span className="text-[var(--text-muted)]">· {formatDuration(routeSeconds)}</span>}
            </span>
            {routeMeters == null && !loading && (
              <span className="text-3xs text-[var(--text-muted)] normal-case">{t('spot.proximity.straight_line', 'luftlinje')}</span>
            )}
          </div>

          {amenity.openingHours && (
            <div className="flex items-start gap-2 text-2xs text-[var(--text-secondary)]">
              <Clock size={13} className="text-amber-400 shrink-0 mt-0.5" />
              <span className="leading-relaxed break-words">{amenity.openingHours}</span>
            </div>
          )}

          <button
            onClick={() => openExternalNavigation(amenity.location.latitude, amenity.location.longitude)}
            className="w-full bg-emerald-600 text-white py-3 rounded-xl text-2xs font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:bg-emerald-500 active:scale-95 transition-all"
          >
            <Navigation size={14} /> {t('spot.proximity.navigate_to_store', { defaultValue: 'Naviger hit' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NearbyAmenityRouteOverlay;
